// src/components/dashboard/IncidentQueueTable.js

import { getIncidentSeverity } from "../../constants/severity";
import { getLaneForCategory } from "../../constants/lanes";
import { generateReportId } from "../../services/incidentReportService";
import { formatDuration } from "../../utils/duration";
import { incidentAgeMs } from "../../utils/queueMetrics";

/**
 * The unclaimed incident queue, as a queue.
 *
 * This replaced a two-column grid of tall cards. The ordering was already
 * correct — worst first, via compareBySeverity — but the presentation threw that
 * away: triaging five incidents meant scrolling past five paragraphs of
 * description. Rows put severity, age, lane, and match evidence on one line so
 * the top of the list is the thing to work on next.
 */

function MatchCell({ matchScore, isStrongMatch, matchReasons = [] }) {
  if (typeof matchScore !== "number") {
    return <span className="text-xs text-slate-400">—</span>;
  }

  return (
    <div className="min-w-0">
      <span
        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${
          isStrongMatch
            ? "border-emerald-200 bg-emerald-50 text-emerald-700"
            : "border-slate-200 bg-slate-50 text-slate-600"
        }`}
      >
        {isStrongMatch ? "Strong match" : "Partial match"}
      </span>
      {matchReasons.length > 0 && (
        <p className="mt-1 truncate text-xs text-slate-500" title={matchReasons.join(" · ")}>
          {matchReasons[0]}
        </p>
      )}
    </div>
  );
}

function QueueRow({ row, onReview, claimingId }) {
  const { problem, matchScore, isStrongMatch, matchReasons } = row;
  const severity = getIncidentSeverity(problem);
  const lane = getLaneForCategory(problem.categoryId || problem.category);
  const ageMs = incidentAgeMs(problem);
  const claiming = claimingId === problem.id;

  return (
    <li
      className={`flex flex-wrap items-center gap-x-4 gap-y-2 border-l-2 px-4 py-3 transition hover:bg-slate-50 ${severity.accentClass}`}
    >
      <div className="flex w-24 flex-shrink-0 items-center">
        <span
          className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-semibold ${severity.chipClass}`}
          title={severity.description}
        >
          <span
            className={`h-1.5 w-1.5 rounded-full ${severity.dotClass}`}
            aria-hidden="true"
          />
          {severity.shortLabel}
        </span>
      </div>

      <div className="min-w-0 flex-1 basis-64">
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs text-slate-400">
            {generateReportId(problem.id)}
          </span>
        </div>
        <p className="truncate text-sm font-medium text-slate-900" title={problem.title}>
          {problem.title || "Untitled incident"}
        </p>
        {problem.tags?.length > 0 && (
          <p className="mt-0.5 truncate text-xs text-slate-500">
            {problem.tags.slice(0, 4).map((tag) => `#${tag}`).join(" ")}
            {problem.tags.length > 4 ? ` +${problem.tags.length - 4}` : ""}
          </p>
        )}
      </div>

      <div className="w-20 flex-shrink-0">
        <p className="text-xs uppercase tracking-wide text-slate-400 md:hidden">
          Waiting
        </p>
        <p className="text-sm font-semibold tabular-nums text-slate-800">
          {formatDuration(ageMs)}
        </p>
      </div>

      <div className="hidden w-32 flex-shrink-0 lg:block">
        <p className="truncate text-sm text-slate-600">
          {lane?.shortLabel || "—"}
        </p>
      </div>

      <div className="hidden w-40 flex-shrink-0 xl:block">
        <MatchCell
          matchScore={matchScore}
          isStrongMatch={isStrongMatch}
          matchReasons={matchReasons}
        />
      </div>

      <div className="flex-shrink-0">
        <button
          type="button"
          onClick={() => onReview(problem)}
          disabled={claiming}
          className="rounded-lg bg-blue-600 px-3.5 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {claiming ? "Claiming…" : "Review & claim"}
        </button>
      </div>
    </li>
  );
}

export default function IncidentQueueTable({
  rows = [],
  onReview,
  claimingId,
  emptyTitle = "Nothing in the queue",
  emptyBody,
}) {
  if (rows.length === 0) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white px-6 py-10 text-center">
        <p className="text-sm font-semibold text-slate-900">{emptyTitle}</p>
        {emptyBody && (
          <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">
            {emptyBody}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      {/* Column headings only earn their space once the columns exist. */}
      <div className="hidden flex-wrap items-center gap-x-4 border-b border-slate-100 bg-slate-50 px-4 py-2 text-xs font-medium uppercase tracking-wide text-slate-500 md:flex">
        <span className="w-24 flex-shrink-0">Severity</span>
        <span className="min-w-0 flex-1 basis-64">Incident</span>
        <span className="w-20 flex-shrink-0">Waiting</span>
        <span className="hidden w-32 flex-shrink-0 lg:block">Lane</span>
        <span className="hidden w-40 flex-shrink-0 xl:block">Match</span>
        <span className="w-30 flex-shrink-0" aria-hidden="true" />
      </div>

      <ul className="divide-y divide-slate-100">
        {rows.map((row) => (
          <QueueRow
            key={row.problem.id}
            row={row}
            onReview={onReview}
            claimingId={claimingId}
          />
        ))}
      </ul>
    </div>
  );
}
